/**
 * The five-step tour of how SchoolVerse fits together.
 *
 * Every screen here is legible on its own. What is not obvious is the chain — plan a unit,
 * schedule it as learning events, teach and mark, and that marked work is what gives a
 * student a standing. A teacher who understands each tab but not the sequence still cannot
 * use the product, so that sequence is the only thing this tour is about. It explains the
 * model, never the buttons: buttons move, and a tour that names them dates in a fortnight.
 *
 * Two rules make it survive a product that is still changing.
 *
 * Anchors are `data-tour` attributes, never CSS classes or positions — a class is a styling
 * decision someone will change without knowing a tour depends on it, whereas `data-tour` is
 * a contract, and it is greppable. `tour.test.ts` fails the build if one goes missing.
 *
 * And a step whose anchor has vanished is skipped, never fatal. The alternative is that one
 * refactor quietly breaks onboarding for every new teacher, and the people it breaks for are
 * the least likely to tell us.
 */

import { navigateTo } from "./navigator";

export interface TourStep {
  /** Matches `data-tour="…"` in the markup. Checked by the test. */
  anchor: string;
  route: string;
  title: string;
  body: string;
}

/*
 * Bump this when the tour changes enough that people who have seen the old one should see
 * the new one. Everyone gets it again; nobody has to be told to go looking.
 */
const SEEN_KEY = "schoolverse.tour.teacher.v1";

export const TOUR: TourStep[] = [
  {
    anchor: "nav.unit-plans",
    route: "/teacher/unit-plans",
    title: "Everything starts with a unit",
    body: "A unit plan is a scheme of work broken into its lessons. You plan it once, and then teach it to as many classes as you like — the plan and the teaching of it are separate things here.",
  },
  {
    anchor: "nav.events",
    route: "/teacher/events",
    title: "A plan becomes a timetable",
    body: "Schedule a unit against a class and every lesson in it becomes a learning event: one lesson, one class, one slot. This is what you teach from, and where the answers come back to be marked.",
  },
  {
    anchor: "nav.assessments",
    route: "/teacher/assessments",
    title: "Papers sit alongside lessons",
    body: "An assessment is set to a whole class or to particular students. SchoolVerse marks it first and you confirm — and it counts towards the same picture as the work done in lessons.",
  },
  {
    anchor: "nav.students",
    route: "/teacher/students",
    title: "Which is how a student gets a standing",
    body: "Marked work, from lessons and papers alike, is the evidence. That is what “secure” or “not yet” means against a student — never a guess, and never an average of everything they have ever done.",
  },
  {
    anchor: "nav.dashboard",
    route: "/teacher",
    title: "Start here each day",
    body: "What is running now, what you are teaching next, and what is waiting on your marking. You can replay this walkthrough any time from “Show me around”.",
  },
];

export function hasSeenTour(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    /* Private browsing: treat as unseen rather than refusing to run. */
    return false;
  }
}

export function markTourSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* Nothing to do — they will see it again next time, which is the harmless failure. */
  }
}

/**
 * Wait for a step's anchor to be real, measurable, and *not the one we just navigated away
 * from*.
 *
 * The shell is rendered per screen, so for a frame or two after a route change the previous
 * screen's nav is still in the document. Matching the selector is therefore not enough: the
 * node found first is the outgoing one, and by the time driver.js measures it, it has been
 * unmounted and reports a zero rect — which puts the popover in the top-left corner of the
 * window, over the very thing it is pointing at.
 */
function waitForAnchor(anchor: string, stale: Element | null, timeoutMs = 2500): Promise<Element | null> {
  const selector = `[data-tour="${anchor}"]`;

  const usable = (): Element | null => {
    const el = document.querySelector(selector);
    if (!el || el === stale || !el.isConnected) return null;
    /* A node mid-unmount, or one inside a collapsed layout, measures zero. */
    return el.getBoundingClientRect().width > 0 ? el : null;
  };

  /*
   * Polled on a timer, not on animation frames. Browsers stop servicing rAF in a background
   * tab, so a teacher who switched away mid-tour — or simply had it open behind something
   * else — would leave this waiting forever, with no step shown and no timeout either.
   */
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const el = usable();
      if (el) return resolve(el);
      if (Date.now() - started > timeoutMs) return resolve(null);
      window.setTimeout(tick, 50);
    };
    tick();
  });
}

/**
 * Run the tour.
 *
 * Each step navigates to its screen first and only then highlights, because the shell is
 * rendered per screen — a route change replaces the nav element, so highlighting before
 * navigating would leave the spotlight on a node that is no longer in the document. That
 * same fact is why navigation goes through `navigateTo` rather than a captured `useNavigate`:
 * the component that started the tour is gone by the second step.
 */
/*
 * One tour at a time. Two instances each navigating on their own schedule fight over the
 * screen — each one's anchor check sees the other's route change and gives up on a step
 * that was perfectly fine. A second click on "Show me around" should be a no-op.
 */
let running = false;

export async function startTour(): Promise<void> {
  if (running) return;
  running = true;

  const [{ driver }] = await Promise.all([
    import("driver.js"),
    import("driver.js/dist/driver.css"),
  ]);

  const instance = driver({
    showProgress: true,
    overlayColor: "#11141c",
    overlayOpacity: 0.6,
    stagePadding: 6,
    stageRadius: 9,
    popoverClass: "schoolverse-tour",
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Got it",
    progressText: "{{current}} of {{total}}",
    /*
     * Always to the right. Every anchor is a nav item hard against the left edge, and left
     * to itself driver.js will happily place the popover on top of the very thing it is
     * pointing at.
     */
    steps: TOUR.map((step) => ({
      element: `[data-tour="${step.anchor}"]`,
      popover: {
        title: step.title,
        description: step.body,
        side: 'right' as const,
        align: 'start' as const,
      },
    })),
    /* Manual control, so a step can navigate and a missing anchor can be stepped over. */
    onNextClick: () => void move(1),
    onPrevClick: () => void move(-1),
    /*
     * Every way out runs `finish`, rather than trusting one hook to fire on all of them.
     * Closing with the × does not reach `onDestroyed` in this version, which left the guard
     * above stuck on and the walkthrough impossible to reopen without a page reload.
     */
    onCloseClick: () => {
      finish();
      instance.destroy();
    },
    onDestroyed: finish,
  });

  /** Idempotent: the last thing to happen however the tour ends. */
  function finish(): void {
    running = false;
    document.removeEventListener("keydown", onKey, true);
    markTourSeen();
  }

  /*
   * Escape closes it. driver.js has its own keyboard handling, but it did not fire here —
   * and an overlay you cannot dismiss from the keyboard is not one to ship on a guess.
   */
  function onKey(event: KeyboardEvent): void {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    finish();
    instance.destroy();
  }
  document.addEventListener("keydown", onKey, true);

  async function move(direction: 1 | -1): Promise<void> {
    let index = instance.getActiveIndex() ?? 0;
    for (;;) {
      index += direction;
      if (index < 0 || index >= TOUR.length) {
        finish();
        instance.destroy();
        return;
      }
      if (await goTo(index)) return;
    }
  }

  /** Navigate to a step and highlight it. False when its anchor never arrived. */
  async function goTo(index: number): Promise<boolean> {
    const step = TOUR[index]!;
    const onThisRouteAlready = window.location.pathname === step.route;
    /* Remember the outgoing node so we can tell it apart from the one that replaces it. */
    const stale = onThisRouteAlready ? null : document.querySelector(`[data-tour="${step.anchor}"]`);

    navigateTo(step.route);
    if (await waitForAnchor(step.anchor, stale)) {
      /* `moveTo` only moves a tour that is already running; `drive` is what starts one. */
      if (instance.isActive()) instance.moveTo(index);
      else instance.drive(index);
      return true;
    }
    // Skipped rather than fatal — see the note at the top of this file.
    console.warn(`[tour] no element with data-tour="${step.anchor}"; skipping that step`);
    return false;
  }

  /* Find the first step that actually has somewhere to point. */
  for (let i = 0; i < TOUR.length; i += 1) {
    if (await goTo(i)) return;
  }
  console.warn("[tour] not one anchor was found; there is nothing to show");
  finish();
}
